import { useState, useEffect, useRef } from 'react';
import { TeamState } from '@/types';

interface TimeoutModalProps {
  teamA: TeamState;
  teamB: TeamState;
  currentSetNumber: number;
  activeTimeout?: {
    id: string;
    teamId: string;
    teamName: string;
    startTimestamp: string;
  } | null;
  timeoutCounts?: Map<string, number>;
  onStartTimeout: (teamId: string, teamName: string) => Promise<void>;
  onEndTimeout: () => Promise<void>;
  onClose: () => void;
}

export default function TimeoutModal({
  teamA,
  teamB,
  currentSetNumber,
  activeTimeout,
  timeoutCounts,
  onStartTimeout,
  onEndTimeout,
  onClose,
}: TimeoutModalProps) {
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [stage, setStage] = useState<'select' | 'running'>(
    activeTimeout ? 'running' : 'select'
  );
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const [localTimeout, setLocalTimeout] = useState<{
    teamId: string;
    teamName: string;
    startTimestamp: string;
  } | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Use either the prop (from parent/recovery) or local state (just started)
  const timeoutData = activeTimeout || localTimeout;

  // Initialize selected team from active timeout if present
  useEffect(() => {
    if (activeTimeout) {
      setSelectedTeamId(activeTimeout.teamId);
      setStage('running');
    }
  }, [activeTimeout]);

  // Timer effect
  useEffect(() => {
    if (stage === 'running' && timeoutData?.startTimestamp) {
      const startTime = new Date(timeoutData.startTimestamp).getTime();

      // Calculate initial elapsed time
      const updateElapsed = () => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedSeconds(elapsed);
      };

      updateElapsed();
      timerRef.current = setInterval(updateElapsed, 1000);

      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [stage, timeoutData?.startTimestamp]);

  const handleTeamSelect = (teamId: string) => {
    setSelectedTeamId(teamId);
  };

  const handleStartTimeout = async () => {
    if (!selectedTeamId) return;

    setIsSubmitting(true);
    try {
      const team = selectedTeamId === teamA.id ? teamA : teamB;
      await onStartTimeout(selectedTeamId, team.name);
      setLocalTimeout({
        teamId: selectedTeamId,
        teamName: team.name,
        startTimestamp: new Date().toISOString(),
      });
      setStage('running');
    } catch (err) {
      console.error('[TimeoutModal] Failed to start timeout:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate timeout number for display: completed count + 1
  const timeoutNumber = timeoutData
    ? (timeoutCounts?.get(timeoutData.teamId) ?? 0) + 1
    : 1;

  const handleEndTimeout = async () => {
    setIsSubmitting(true);
    setEndError(null);
    try {
      await onEndTimeout();
      if (timerRef.current) clearInterval(timerRef.current);
      onClose();
    } catch (err) {
      console.error('[TimeoutModal] Failed to end timeout:', err);
      setEndError('Failed to end timeout. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format elapsed time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const teamAStyle = { borderColor: teamA.color, backgroundColor: `${teamA.color}15` };
  const teamBStyle = { borderColor: teamB.color, backgroundColor: `${teamB.color}15` };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl p-5 w-full max-w-sm shadow-2xl">
        {/* Header */}
        {stage === 'select' && (
          <>
            <h2 className="text-lg font-bold text-text-primary text-center mb-4">
              Select Team Taking Timeout
            </h2>

            {/* Team Selection */}
            <div className="space-y-3 mb-5">
              <button
                onClick={() => handleTeamSelect(teamA.id)}
                className={`w-full p-4 rounded-lg border-2 transition-all ${
                  selectedTeamId === teamA.id ? 'border-team-a' : 'border-border'
                }`}
                style={selectedTeamId === teamA.id ? teamAStyle : undefined}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: teamA.color }}
                    />
                    <span className="font-bold text-text-primary">{teamA.name}</span>
                  </div>
                  {selectedTeamId === teamA.id && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-team-a">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              </button>

              <button
                onClick={() => handleTeamSelect(teamB.id)}
                className={`w-full p-4 rounded-lg border-2 transition-all ${
                  selectedTeamId === teamB.id ? 'border-team-b' : 'border-border'
                }`}
                style={selectedTeamId === teamB.id ? teamBStyle : undefined}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: teamB.color }}
                    />
                    <span className="font-bold text-text-primary">{teamB.name}</span>
                  </div>
                  {selectedTeamId === teamB.id && (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-team-b">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              </button>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={handleStartTimeout}
                disabled={!selectedTeamId || isSubmitting}
                className="w-full py-4 px-6 rounded-xl font-bold text-base bg-warning text-black transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    STARTING...
                  </span>
                ) : (
                  'START TIMEOUT'
                )}
              </button>
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="w-full py-3 px-6 rounded-xl font-semibold text-sm bg-surface border border-border text-text-secondary transition-all active:scale-[0.98] hover:bg-surface-raised disabled:opacity-50"
              >
                CANCEL
              </button>
            </div>
          </>
        )}

        {/* Running Timeout */}
        {stage === 'running' && timeoutData && (
          <>
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full bg-warning animate-pulse" />
                <span className="text-sm font-bold uppercase text-warning tracking-wider">
                  TIMEOUT {timeoutNumber} IN PROGRESS
                </span>
              </div>
              <p className="text-xs text-text-muted">
                Set {currentSetNumber}
              </p>
            </div>

            {/* Team Taking Timeout */}
            <div
              className="p-4 rounded-lg mb-4 text-center"
              style={{
                borderColor: selectedTeamId === teamA.id ? teamA.color : teamB.color,
                backgroundColor: selectedTeamId === teamA.id ? `${teamA.color}15` : `${teamB.color}15`,
                borderWidth: 2,
              }}
            >
              <p className="text-sm text-text-muted mb-1">Team</p>
              <p
                className="text-xl font-bold"
                style={{ color: selectedTeamId === teamA.id ? teamA.color : teamB.color }}
              >
                {timeoutData.teamName}
              </p>
            </div>

            {/* Timer */}
            <div className="text-center mb-5">
              <p className="text-sm text-text-muted mb-1">Elapsed Time</p>
              <p className="text-5xl font-mono font-bold text-text-primary tracking-wider">
                {formatTime(elapsedSeconds)}
              </p>
            </div>

            {endError && (
              <p className="text-error text-sm text-center mb-3">{endError}</p>
            )}

            {/* End Timeout Button */}
            <button
              onClick={handleEndTimeout}
              disabled={isSubmitting}
              className="w-full py-4 px-6 rounded-xl font-bold text-base bg-success text-white transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ENDING...
                </span>
              ) : (
                'END TIMEOUT'
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
